import { IntegratedModel, ProductDetailsResponsePayload } from '../types';
import { isEqualStrings } from './utils';

export const getIntegratedModels = (
    productDetails: Array<ProductDetailsResponsePayload>,
    copilotModelNames: Array<string>
): Array<IntegratedModel> => {
    let triageModelNames = new Set<string>();
    const nameToProductIdsMap = {};

    const triageModels =
        productDetails?.map((pd) => {
            triageModelNames.add(pd.model.name.toLowerCase());
            if (pd.model.display) {
                triageModelNames.add(pd.model.display.toLowerCase());
            }
            const productIds = {
                productTypeId: pd.producttype.id,
                manufacturerId: pd.manufacturer.id,
                productId: pd.model.id
            };
            pd.model.knowledge_names?.forEach(
                (name) => (nameToProductIdsMap[name.toLowerCase()] = productIds)
            );
            return {
                modelName: pd.model.name,
                modelDisplay: pd.model.display,
                productTypeName: pd.producttype.name,
                productTypeDisplay: pd.producttype.display,
                manufacturerName: pd.manufacturer.name,
                manufacturerDisplay: pd.manufacturer.display,
                productIds
            };
        }) || [];
    const copilotModels =
        copilotModelNames
            ?.filter(
                (modelName) => !triageModelNames.has(modelName.toLowerCase())
            )
            .map((modelName) => {
                const productIds = nameToProductIdsMap[modelName.toLowerCase()];
                return {
                    modelName,
                    productIds,
                    isLinkedProductDetails: !!productIds
                };
            }) || [];
    const copilotModelNamesSet = new Set(
        copilotModelNames.map((name) => name.toLowerCase())
    );

    return [
        ...triageModels.filter(
            (model) =>
                copilotModelNamesSet.has(model.modelName?.toLowerCase()) ||
                copilotModelNamesSet.has(model.modelDisplay?.toLowerCase())
        ),
        ...copilotModels
    ];
};

export const getIntegratedModelDisplay = (model: IntegratedModel) =>
    model?.modelDisplay || model?.modelName || '';

export const adjustIntegratedModels = (
    integratedModels: Array<IntegratedModel>
) => {
    return integratedModels.map((model) => adjustIntegratedModel(model));
};

export const adjustIntegratedModel = (integratedModel: IntegratedModel) => {
    const productTypeName =
        integratedModel.productTypeDisplay ||
        integratedModel.productTypeName ||
        '';
    const manufacturerName =
        integratedModel.manufacturerDisplay ||
        integratedModel.manufacturerName ||
        '';

    const ptText =
        productTypeName && manufacturerName
            ? `(${productTypeName} \u2022 ${manufacturerName})`
            : productTypeName
            ? `(${productTypeName})`
            : manufacturerName
            ? `(${manufacturerName})`
            : '';

    return `${getIntegratedModelDisplay(integratedModel)} ${ptText}`;
};

export const getModelNameToObject = (
    integratedModels: Array<IntegratedModel>,
    shouldUseOnlyModelName = false
): Record<string, IntegratedModel> => {
    const nameToObject: Record<string, IntegratedModel> = {};
    integratedModels.forEach((model: IntegratedModel) => {
        if (shouldUseOnlyModelName) {
            nameToObject[model.modelName] = model;
        } else {
            nameToObject[adjustIntegratedModel(model)] = model;
        }
    });
    return nameToObject;
};

export const getModelFromIntegratedModels = (
    integratedModels: Array<IntegratedModel>,
    model: string,
    productType?: string,
    manufacturer?: string
): IntegratedModel | undefined => {
    if (productType && manufacturer) {
        return integratedModels?.find(
            (integrateModel) =>
                (isEqualStrings(integrateModel.modelName, model) ||
                    isEqualStrings(integrateModel.modelDisplay, model)) &&
                (isEqualStrings(integrateModel.productTypeName, productType) ||
                    isEqualStrings(
                        integrateModel.productTypeDisplay,
                        productType
                    )) &&
                (isEqualStrings(
                    integrateModel.manufacturerName,
                    manufacturer
                ) ||
                    isEqualStrings(
                        integrateModel.manufacturerDisplay,
                        manufacturer
                    ))
        );
    }
    return integratedModels?.find(
        (integrateModel) =>
            isEqualStrings(integrateModel.modelName, model) ||
            isEqualStrings(integrateModel.modelDisplay, model)
    );
};

export const getModelFromIntegratedModelsByIds = (
    integratedModels: Array<IntegratedModel>,
    modelName: string,
    productTypeId?: string,
    manufacturerId?: string
): IntegratedModel | undefined => {
    if (productTypeId && manufacturerId) {
        return integratedModels?.find(
            (integratedModel) =>
                isEqualStrings(integratedModel.modelName, modelName) &&
                isEqualStrings(
                    integratedModel.productIds.productTypeId,
                    productTypeId
                ) &&
                isEqualStrings(
                    integratedModel.productIds.manufacturerId,
                    manufacturerId
                )
        );
    }
    return integratedModels?.find((integratedModel) =>
        isEqualStrings(integratedModel.modelName, modelName)
    );
};

export const checkIfModelExistsInTriage = (
    integratedModels: Array<IntegratedModel>,
    modelName: string
) => {
    return integratedModels.some(
        (integratedModel) =>
            integratedModel.modelName === modelName &&
            !!integratedModel.productIds
    );
};

export const getIntegratedModelsByName = (
    integratedModels: IntegratedModel[],
    modelName: string
) =>
    integratedModels.filter(
        (integrateModel) =>
            isEqualStrings(integrateModel.modelName, modelName) ||
            isEqualStrings(integrateModel.modelDisplay, modelName)
    );
